import React, { useEffect, useState, useMemo } from "react";
import {shallowEqual, useDispatch, useSelector} from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Card, Grid, Typography, FormControl, Select, MenuItem, InputLabel } from "@mui/material";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import {commentsReport, getSurveyLookupFiling} from "../../pages/Survey/Survey.service";
import dayjs from "dayjs";
import { getTextFromHTML, htmlParser } from "../../Utility/constants";
import {fetchPrintSurveyMenu, fetchSurveyFiling} from "../../pages/Survey/Survey.slice";


const GetComments = ({ handleCloseComments }) => {
  const { state } = useLocation();
  const dispatch = useDispatch();
  const surveyName = useSelector(
      (state) => state.survey.activeSurveys[0]?.name
  );
  const surveyFilingId = useSelector(
      (state) => state.survey.surveyFiling[0]?.id
  );
  const surveyRespondentId = useSelector(
      (state) => state.survey.respondentDetails?.id
  );

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState([]);
  const [surveyComments, setSurveyComments] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [sortBy, setSortBy] = useState(null);
  const [sortAscending, setSortAscending] = useState(null);
  const initialColumns = {
    dateCreated: true,
    commentingOrg: true,
    createdBy: true,
    comment: true,
    sectionName: true,
    pageName: true,
    question: true,
    errorText: true,
    ruleId: true,
    orgName: true,
  };
  const surveyMenu = useSelector((state) => state.survey.printSurveyMenu, shallowEqual);
  const [surveyId, setSurveyId] = useState('');
  let [selectedSurveyFilingId, setSelectedSurveyFilingId] = useState('');

  useEffect(() => {
    if (surveyMenu.length == 0) {
      dispatch(fetchPrintSurveyMenu({ surveyFilingId, surveyRespondentId }))
          .then((response) => {
            if (response?.payload && response?.payload.surveys?.length > 0) {
                setSurveyId(response.payload.surveys[0].id);
            }
          });
    } else if (surveyMenu?.surveys?.length > 0) {
      if (!surveyId) {
        setSurveyId(surveyMenu.surveys[0].id);
      } else {
        getSurveyLookupFiling({ surveyId, surveyRespondentId })
        .then((response) => {
          if (response?.data && response?.data.length > 0) {
            setSelectedSurveyFilingId(response?.data[0].id);
          }
        });
      }
    }
  }, [surveyMenu, dispatch, surveyId]);

  useEffect(() => {
    if (selectedSurveyFilingId) {
      fetchCommentsReport(selectedSurveyFilingId);
    }
  }, [selectedSurveyFilingId, pagination.pageIndex, pagination.pageSize]);

  const handleSurveyChange = async (event) => {
    if (event?.target?.value) {
      setSurveyId(event?.target?.value);
      setPagination({pageIndex: 0, pageSize: 10});
    }
  };

  const fetchCommentsReport = async (survFilingId) => {
    try {
      const payload = {
        surveyFilingId: survFilingId,
        pageNumber: pagination.pageIndex + 1,
        numberPerPage: pagination.pageSize,
        sortBy: sortBy,
        sortAscending: sortAscending,
      };
      const res = await commentsReport(payload);
      if (res?.data) {
        setSurveyComments(res?.data?.comments);
        setRowCount(res?.data?.totalNumber);
      } else {
        setSurveyComments([]);
      }
    } catch (error) {
      console.error("Error fetching comments report:", error);
    }
  };
  const data = useMemo(() => {
    return surveyComments?.map((row) => {
      return {
        dateCreated: dayjs
          .utc(row?.dateCreated)
          .local()
          .format("MM-DD-YYYY hh:mm A"),
        comment: row?.comment,
        commentingOrg: row?.commentingOrg,
        createdBy: row?.createdBy,
        sectionName: getTextFromHTML(row?.sectionName),
        pageName: getTextFromHTML(row?.pageName),
        question: row?.question,
        errorText: row?.errorText,
        ruleId: row?.ruleId,
        orgName: row?.orgName,
        color: row?.color,
      };
    });
  }, [surveyComments]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "dateCreated",
        header: "Date Created",
      },
      {
        accessorKey: "commentingOrg",
        header: "Commenting Org.",
      },
      {
        accessorKey: "createdBy",
        header: "Created By",
      },
      {
        accessorKey: "comment",
        header: "Comment",
        Cell: ({ row }) => (
          <>
            {!!row?.original?.color && (
              <span
                style={{
                  color: row.original.color,
                }}
              >
                {htmlParser(row?.original?.comment)}
              </span>
            )}
          </>
        ),
      },
      {
        accessorKey: "sectionName",
        header: "Section Name",
      },
      {
        accessorKey: "pageName",
        header: "Page Name",
      },
      {
        accessorKey: "question",
        header: "Question",
        Cell: ({ row }) => (
          <>
            {!!row?.original?.question && (
              <span>{htmlParser(row?.original?.question)}</span>
            )}
          </>
        ),
      },
      {
        accessorKey: "errorText",
        header: "Error Text",
        Cell: ({ row }) => (
          <>
            {!!row?.original?.errorText && (
              <span>{htmlParser(row?.original?.errorText)}</span>
            )}
          </>
        ),
      },
      {
        accessorKey: "ruleId",
        header: "Rule Id",
      },
      {
        accessorKey: "orgName",
        header: "Org. Name",
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    manualPagination: true,
    manualSorting: true,
    columns,
    data,
    onPaginationChange: setPagination,
    state: { pagination, sorting },
    onSortingChange: setSorting,
    rowCount,
    initialState: { density: "compact" },
    enableColumnFilters: false,
    enableFullScreenToggle: false,
    enableDensityToggle: false,
    enableHiding: false,
    muiTableBodyCellProps: {
      sx: {
        fontSize: "1rem",
        opacity: "unset",
      },
    },

    muiTableHeadProps: {
      sx: {
        opacity: "unset",
      },
    },
    defaultColumn: {
      maxSize: 300,
      minSize: 80,
      size: 160, //default size is usually 180
    },
  });
  return (
    <div className="panelModal">
      <div className="panelDiv">
        <Typography variant="label">
          Comments for {getTextFromHTML(surveyName)}
        </Typography>
      </div>
      <div className="panelForm">
        <Typography variant="body1">
          This page will allow you to track any changes made to your survey by
          the survey administrative representatives once your survey has been
          accepted in its entirety. Please feel free to monitor this page once
          you have submitted your survey.
        </Typography>

        <Grid item xs={2}>
          <div>
            <Typography sx={{ fontWeight: "bold" }} mb={1}>
              {"Select survey:"}
            </Typography>
            <FormControl size="small" sx={{ minWidth: "30%" }}>
              <Select
                  id="SurveySelect"
                  value={surveyId}
                  placeholder="Please select survey"
                  onChange={(event) => handleSurveyChange(event)}
              >
                {surveyMenu?.surveys?.map((opt) => (
                    <MenuItem value={opt.id}>{getTextFromHTML(opt.name)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </Grid>

        <MaterialReactTable table={table} />
        <Box sx={{ flexGrow: 1 }} marginTop={5}>
          <Grid container spacing={3} style={{ textAlign: "right" }}>
            <Grid item xs={6}>
              <Button variant="contained" onClick={() => handleCloseComments()}>
                Close
              </Button>
            </Grid>
          </Grid>
        </Box>
      </div>
    </div>
  );
};

export default GetComments;
